// One year's worth of a Captain's Log: every trip logged that year, listed,
// over a map of all of it. The rung between one trip (LogbookTrip.jsx) and the
// whole logbook, reached by clicking a year row on the summary card.
//
// A row is a trip -- the year's OWN SLICE of one, so the rows always add up to
// the card row above them (see tripsInYear). Entries nobody filed under a trip
// name get a row each, because they still happened and the map still draws
// them; theirs leads to the entry rather than to a trip page.
//
// Read-only, on LogbookTrip's terms: this page draws gazetteer-resolved copies
// and never writes one back. Opening a page is not a mutation.

import { useCallback, useMemo, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import { DataCard, Spinner } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import {
  urlForLogbookEntry,
  urlForLogbookForId,
  urlForLogbookTrip,
  urlForLogbookYear,
} from '../../fleet-shared/Urls.mjs';
import {
  listColumnsForType,
  summarizeLogbook,
  LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';
import {
  entriesForYear,
  tripFieldForType,
  tripsInYear,
  summarizeTripEntries,
  tripSummaryParts,
} from '../../fleet-shared/LogbookTrips.mjs';
import { ENTRY_ROUTE_KEY } from '../../fleet-shared/TripRoute.mjs';
import { sortTripsAscending } from '../../fleet-shared/TripFields.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { useLogbookPageEntries } from './useLogbookPageEntries';
import { useEntryMapLegs } from './useEntryMapLegs';
import LogbookEntriesTable from './LogbookEntriesTable';
import LogbookTripsTable from './LogbookTripsTable';
import LogbookViewControls from './LogbookViewControls';
import PhotoErrorAlert from './PhotoErrorAlert';
import { useEntryPhotos } from './useEntryPhotos';
import { useLogbookViewPrefs } from './useLogbookViewPrefs';

// Lazy: the only import path that pulls in maplibre-gl (~250 KB gz).
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

// What a row with no trip name is called. The wording lives here rather than in
// LogbookTrips.mjs, which yields '' and leaves the label to whoever renders it.
const UNFILED = '(no trip name)';

// Four digits, nothing else. `Number('0x7EA')` is 2026 and `Number(' 2026 ')`
// is 2026 -- a garbage segment must render an empty year, not a year nobody
// meant, and must never throw.
function yearFromParam(raw) {
  return /^\d{4}$/.test(raw ?? '') ? Number(raw) : null;
}

const LogbookYear = () => {
  const { itemId, logbookId, year: rawYear } = useParams();
  const location = useLocation();
  const navigate = useRootCrumbNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const year = yearFromParam(rawYear);

  const select = useCallback(
    (allEntries) => (year === null ? [] : entriesForYear(allEntries, year)),
    [year]
  );

  const { item, logbook, entries, allEntries, StatusMessage } = useLogbookPageEntries({
    itemId,
    logbookId,
    selectKey: String(year),
    select,
  });

  // For the map's route overlay and its marker pills. Called unconditionally,
  // as LogbookTrip does: gating it on "does any entry have a route" would need
  // the entries to have landed first, and a usePlaceIndex whose `enabled` flips
  // mid-life is a footgun for no measurable win.
  const placeIndex = usePlaceIndex();

  const typeId = logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;

  // The single "which logbooks have trips" expression. A type without one
  // (a Pilot's or Maintenance deep link) has no trips view to offer: the page
  // is entries-only and shows no toggle, rather than the one-row-per-entry
  // pseudo-trips table it used to draw.
  const tripField = tripFieldForType(typeId);

  // Which view: trips is this page's default, so only `?view=entries` is ever
  // in the URL (TripsCalendarSection's contract -- absent means the default
  // and is never written back; every write replaces, so Back leaves the page
  // rather than walking through views).
  const view = tripField && searchParams.get('view') !== 'entries' ? 'trips' : 'entries';
  const setView = useCallback(
    (next) => {
      setSearchParams(
        (params) => {
          const updated = new URLSearchParams(params);
          if (next === 'entries') updated.set('view', 'entries');
          else updated.delete('view');
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // The persisted Show notes / Show photos switches, shared with the other
  // logbook lists.
  const [viewPrefs, setViewPref] = useLogbookViewPrefs();

  // ONE item-wide S3 listing for the page, keyed on itemId alone -- so this
  // year's slice and the big log's whole list cost the same read, and stepping
  // to the next year through the header chevrons re-uses it (`loadedFor`
  // short-circuits on an unchanged itemId). The switch is the hook's `enabled`
  // AND what both tables below are given: one boolean, so "we fetched" and "we
  // render" cannot disagree.
  const { photosByEntry, photosError, openPhoto } = useEntryPhotos(itemId, {
    enabled: viewPrefs.showPhotos,
  });

  // Same mirror-then-sort as LogbookTrip: the query comes back newest-first,
  // and reversing is the only thing that orders entries sharing one date.
  const orderedEntries = useMemo(
    () => (entries ? sortTripsAscending([...entries].reverse()) : null),
    [entries]
  );

  const { displayEntries, mapLegs, anyOnMap } = useEntryMapLegs(orderedEntries, placeIndex);

  const rows = useMemo(
    () => (entries ? tripsInYear(entries, typeId, year) : null),
    [entries, typeId, year]
  );

  const summary = useMemo(
    () => (entries ? summarizeTripEntries(entries, typeId) : null),
    [entries, typeId]
  );

  // Which chain each entry's line belongs to, for the map's marker pruning.
  // A year is many separate trips, so TripGroupMap's default -- treat every
  // leg as one end-to-end run and give only the very last one an end marker --
  // would drop every trip's final destination but the year's last.
  //
  // Read off `rows` rather than recomputed: tripsInYear already keys entries by
  // trip name and already gives an unfiled entry a row of its own, which is
  // exactly the chaining the map wants. One source, so the map can't disagree
  // with the table under it.
  const chainKeyByEntryId = useMemo(() => {
    const byId = new Map();
    (rows || []).forEach((row) => row.entries.forEach((entry) => byId.set(entry.id, row.key)));
    return byId;
  }, [rows]);

  const heading = year === null ? 'Year' : String(year);

  // Where "back" leads from an entry opened off this page: this year, in the
  // view the user was looking at. The entry page's breadcrumbs render
  // `location.state.back` as an extra crumb -- without it, year -> entry has
  // no trail back to the year at all.
  const backCrumb = useMemo(
    () => ({ name: heading, url: urlForLogbookYear(itemId, logbookId, rawYear) + location.search }),
    [heading, itemId, logbookId, rawYear, location.search]
  );

  // A named row opens the trip page -- WITHOUT handing over its entries. This
  // row holds only the year's slice of that trip, and a trip crossing New
  // Year's Eve would paint there as though the other year never happened.
  //
  // An unfiled row has no trip to open, so it opens its one entry.
  const onSelectRow = useCallback(
    (row) => {
      if (row.name) {
        // `row.key` IS the trip's key (tripsInYear builds it with entryTripKey),
        // so the link needs no second derivation -- and two rows both labelled
        // "Day" open the two different trips they actually are. The name rides
        // along only so the heading doesn't wait for the fetch.
        navigate(urlForLogbookTrip(itemId, logbookId, row.key), {
          state: { item, logbook, tripName: row.name },
        });
        return;
      }
      const entry = row.entries[0];
      if (!entry) return;
      navigate(urlForLogbookEntry(itemId, logbookId, entry.id), {
        state: { item, logbook, fromLogbookList: true, back: backCrumb },
      });
    },
    [itemId, logbookId, item, logbook, navigate, backCrumb]
  );

  // The map's navigator, LogbookTrip's verbatim: re-look-up the STORED entry by
  // id rather than hand over the resolved copy the map is drawing. The entries
  // view's table rows route through here too -- its rows are display copies on
  // the same terms as the map's legs.
  const onSelectEntry = useCallback(
    (leg) => {
      const stored = (entries || []).find((entry) => entry.id === leg.id);
      if (!stored) return;
      navigate(urlForLogbookEntry(itemId, logbookId, stored.id), {
        state: { item, logbook, fromLogbookList: true, back: backCrumb },
      });
    },
    [entries, itemId, logbookId, item, logbook, navigate, backCrumb]
  );

  // Every year with entries, newest-first: summarizeLogbook's byYear, the same
  // rule the summary card's rows follow, read off the WHOLE logbook so the
  // chevrons can name years other than this one. Null until that read lands --
  // the chevrons simply appear when it does.
  const years = useMemo(
    () => (allEntries ? summarizeLogbook(allEntries, typeId).byYear.map((row) => row.year) : null),
    [allEntries, typeId]
  );

  // The adjacent years that actually have entries -- stepping lands somewhere
  // with something on it, skipping empty years entirely.
  const previousYear = useMemo(() => {
    if (!years || year === null) return null;
    // Newest-first, so the first smaller year is the nearest earlier one.
    return years.find((y) => y < year) ?? null;
  }, [years, year]);
  const nextYear = useMemo(() => {
    if (!years || year === null) return null;
    const later = years.filter((y) => y > year);
    // Newest-first, so the LAST of the larger years is the nearest later one.
    return later.length ? later[later.length - 1] : null;
  }, [years, year]);

  // Step to another year: Logbook.jsx's onSelectYear handover (the chevrons
  // only render once `allEntries` is in hand, so the slice is always whole),
  // carrying the current search so `?view=` survives the step.
  const goToYear = useCallback(
    (y) => {
      navigate(
        { pathname: urlForLogbookYear(itemId, logbookId, String(y)), search: location.search },
        { state: { item, logbook, entries: entriesForYear(allEntries ?? [], y) } }
      );
    },
    [navigate, itemId, logbookId, location.search, item, logbook, allEntries]
  );

  return (
    <div className="pb-2">
      <ItemBreadcrumbs
        item={item}
        extras={[
          ...(logbook ? [{ name: logbook.name, url: urlForLogbookForId(itemId, logbookId) }] : []),
          { name: heading, url: urlForLogbookYear(itemId, logbookId, rawYear) },
        ]}
      />

      <StatusMessage />

      {anyOnMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap
            legs={mapLegs}
            onSelectLeg={onSelectEntry}
            routeKey={ENTRY_ROUTE_KEY}
            title="Route"
            showWeather={false}
            chainKeyFor={(leg) => chainKeyByEntryId.get(leg.id) ?? leg.id}
          />
        </Suspense>
      )}

      <DataCard
        header={
          <CardHeader
            text={
              // The chevrons step to the nearest year that has entries; each
              // renders only when such a year exists, and neither until the
              // whole-logbook read has landed.
              <span className="d-inline-flex align-items-center gap-2">
                {previousYear !== null && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    aria-label="Previous year"
                    onClick={() => goToYear(previousYear)}
                  >
                    <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
                  </Button>
                )}
                {heading}
                {nextYear !== null && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    aria-label="Next year"
                    onClick={() => goToYear(nextYear)}
                  >
                    <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                  </Button>
                )}
              </span>
            }
          />
        }
        body={
          entries ? (
            <>
              {summary && summary.count > 0 && (
                <p className="text-center text-body-secondary small mb-3" data-testid="year-totals">
                  {tripSummaryParts(summary).join(' · ')}
                </p>
              )}

              {rows.length > 0 && (
                <LogbookViewControls
                  view={view}
                  onViewChange={setView}
                  showViewToggle={Boolean(tripField)}
                  showNotes={viewPrefs.showNotes}
                  onShowNotesChange={(value) => setViewPref('showNotes', value)}
                  showPhotosToggle
                  showPhotos={viewPrefs.showPhotos}
                  onShowPhotosChange={(value) => setViewPref('showPhotos', value)}
                />
              )}

              {/* Above the view branch, so it serves both views. Never the
                  page's own StatusMessage -- see PhotoErrorAlert, which owns
                  the wording and the dismissal. */}
              <PhotoErrorAlert error={photosError} show={viewPrefs.showPhotos} />

              {rows.length === 0 ? (
                <p className="text-center text-body-secondary py-3 mb-0">
                  No entries in this logbook fall in that year.
                </p>
              ) : view === 'trips' ? (
                <LogbookTripsTable
                  rows={rows}
                  typeId={typeId}
                  placeIndex={placeIndex}
                  showNotes={viewPrefs.showNotes}
                  onSelectRow={onSelectRow}
                  onSelectEntry={onSelectEntry}
                  unfiledLabel={UNFILED}
                  photosByEntry={viewPrefs.showPhotos ? photosByEntry : null}
                  onOpenPhoto={openPhoto}
                />
              ) : (
                <LogbookEntriesTable
                  // FULL columns, Trip Name included: unlike the trip page,
                  // these rows span every trip in the year.
                  entries={displayEntries}
                  columns={listColumnsForType(typeId)}
                  placeIndex={placeIndex}
                  showNotes={viewPrefs.showNotes}
                  onSelectEntry={onSelectEntry}
                  photosByEntry={viewPrefs.showPhotos ? photosByEntry : null}
                  onOpenPhoto={openPhoto}
                />
              )}
            </>
          ) : null
        }
      />
    </div>
  );
};

export default LogbookYear;
